import { Avatar, Box } from '@mui/material';
import {
  DateCalendar,
  DateView,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isSameMonth } from 'date-fns';
import { he } from 'date-fns/locale';
import { heIL } from '@mui/x-date-pickers/locales';
import { AccordionArrowBlue, RightArrowDatePicker } from 'Images';

/** MUI DateCalendar onChange selectionState values. */
export type StyledDateCalendarSelectionState = 'partial' | 'shallow' | 'finish';

export interface IStyledDateCalendar {
  value: Date | null;
  minDate: Date;
  maxDate: Date;
  onChange: (
    value: Date | null,
    selectionState?: StyledDateCalendarSelectionState
  ) => void;
  /** Near-term dates only (e.g. commitment) — day grid + month arrows. */
  dayViewOnly?: boolean;
  views?: readonly DateView[];
  openTo?: DateView;
}

const calendarArrowSx = {
  width: '28px',
  height: '28px',
};

/** Calendar UI shared with self-service commitment date selection. */
export const StyledDateCalendar = ({
  value,
  minDate,
  maxDate,
  onChange,
  dayViewOnly = true,
  views: viewsProp,
  openTo = 'day',
}: IStyledDateCalendar) => {
  const views =
    viewsProp ??
    (dayViewOnly ? (['day'] as const) : (['year', 'month', 'day'] as const));
  const showMonthArrows =
    !dayViewOnly || (views.length === 1 && !isSameMonth(minDate, maxDate));

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: 'primary.shade_100',
        borderRadius: '20px',
        boxShadow: '0px 4px 32px 0px rgba(21, 82, 232, 0.1)',
        overflow: 'hidden',
        pb: '13px',
        '.MuiDateCalendar-root': {
          width: '100%',
          maxHeight: 'unset',
          height: 'auto',
          overflow: 'visible',
        },

        '.MuiPickersCalendarHeader-root': {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          borderBottom: '1px solid',
          borderColor: 'primary.shade_100',
          m: 0,
          p: '12px 16px 13px',
          position: 'relative',
        },
        '.MuiPickersCalendarHeader-labelContainer': {
          m: 0,
          order: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
        },
        '.MuiPickersCalendarHeader-label': {
          fontWeight: 700,
          color: 'primary.main',
          fontSize: '18px',
          lineHeight: '24px',
        },
        '.MuiPickersCalendarHeader-switchViewButton': {
          display: dayViewOnly ? 'none' : 'inline-flex',
          color: 'primary.main',
          p: 0,
          mr: '4px',
        },
        '.MuiPickersArrowSwitcher-root': {
          display: showMonthArrows ? 'contents' : 'none',
        },
        '.MuiPickersArrowSwitcher-spacer': {
          display: 'none',
        },
        '.MuiPickersArrowSwitcher-button:first-of-type': {
          order: 1,
        },
        '.MuiPickersArrowSwitcher-button:last-of-type': {
          order: 3,
        },

        ...(!dayViewOnly
          ? {
              '.MuiYearCalendar-root': {
                width: '100%',
                maxHeight: '320px',
                px: '12px',
                boxSizing: 'border-box',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                alignContent: 'flex-start',
              },
              '.MuiPickersYear-root': {
                flexBasis: '25%',
                maxWidth: '25%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              },
              '.MuiPickersYear-yearButton': {
                width: '64px',
                height: '40px',
                flexShrink: 0,
                borderRadius: '20px',
                fontSize: '18px',
                lineHeight: '24px',
                fontWeight: 400,
                mx: 0,
                my: '4px',
              },
              '.MuiMonthCalendar-root': {
                width: '100%',
                px: '12px',
                boxSizing: 'border-box',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
              },
              '.MuiPickersMonth-root': {
                flexBasis: '33.33%',
                maxWidth: '33.33%',
                display: 'flex',
                justifyContent: 'center',
              },
              '.MuiPickersMonth-monthButton': {
                fontSize: '18px',
                lineHeight: '24px',
                fontWeight: 400,
                height: '40px',
                width: '100%',
                maxWidth: '120px',
                borderRadius: '20px',
              },
            }
          : {}),

        '.MuiPickersYear-yearButton, .MuiPickersMonth-monthButton': {
          fontSize: '18px',
          lineHeight: '24px',
          fontWeight: 400,
        },
        '.MuiPickersYear-yearButton.Mui-selected, .MuiPickersMonth-monthButton.Mui-selected':
          {
            backgroundColor: 'primary.main',
            color: '#fff',
            '&:hover, &:focus, &:focus-visible': {
              backgroundColor: 'primary.main',
              outline: 'none',
            },
          },

        '.MuiDayCalendar-slideTransition': {
          minHeight: 'unset',
          overflow: 'visible',
        },
        '.MuiDayCalendar-monthContainer': { position: 'relative' },
        '.MuiDayCalendar-header, .MuiDayCalendar-weekContainer': {
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          m: 0,
          px: '12px',
        },
        '.MuiDayCalendar-weekDayLabel': {
          width: 'auto',
          height: '48px',
          m: 0,
          justifySelf: 'center',
          fontSize: '16px',
          fontWeight: 400,
          letterSpacing: '0.5px',
          color: '#1D1B20',
        },

        '.MuiPickersDay-root': {
          width: '40px',
          height: '40px',
          m: '4px 0',
          justifySelf: 'center',
          fontSize: '18px',
          lineHeight: '24px',
          fontWeight: 400,
          color: '#1D1B20',
        },
        '.MuiPickersDay-root.Mui-disabled': {
          color: '#1D1B20',
          opacity: 0.38,
        },
        '.MuiPickersDay-today:not(.Mui-selected)': {
          border: 'none',
          backgroundColor: 'transparent',
        },
        '.MuiPickersDay-root.Mui-selected': {
          backgroundColor: 'primary.main',
          color: '#fff',
          '&:hover, &:focus': { backgroundColor: 'primary.main' },
        },
      }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={he}
        localeText={
          heIL.components.MuiLocalizationProvider.defaultProps.localeText
        }
      >
        <DateCalendar
          value={value}
          onChange={(newValue, selectionState) =>
            onChange(newValue, selectionState)
          }
          minDate={minDate}
          maxDate={maxDate}
          views={views}
          openTo={openTo}
          disableHighlightToday
          reduceAnimations
          slots={{
            switchViewIcon: (props) => (
              <Avatar
                {...props}
                src={AccordionArrowBlue}
                alt=''
                sx={{ ...calendarArrowSx, width: '24px', height: '24px' }}
              />
            ),
            leftArrowIcon: (props) => (
              <Avatar
                {...props}
                src={RightArrowDatePicker}
                alt=''
                sx={{
                  ...calendarArrowSx,
                  transform: 'rotate(180deg)',
                }}
              />
            ),
            rightArrowIcon: (props) => (
              <Avatar
                {...props}
                src={RightArrowDatePicker}
                alt=''
                sx={calendarArrowSx}
              />
            ),
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};
